import React, { useContext, useEffect } from "react";
import {
  Table,
  Pagination,
  Popup,
  Icon,
  Dropdown,
} from "semantic-ui-react";
import { observer } from "mobx-react-lite";
import { RootStoreContext } from "../../app/stores/rootStore";
import { Link } from "react-router-dom";
import { pageSizeOptions, getProcessResultCode } from "../../app/util/listOfValoursOptions";
import { ProcessResultCode } from "../../app/util/GeneralEnums";
import LoadingComponent from "../../app/layout/LoadingComponent";

interface IProps {
  match: string;
}

interface ISortableHeaderCellProps {
  columnKey: string;
  children: React.ReactNode;
  onSort: (columnName: string) => void;
  getSortDirection: (columnName: string) => 'ascending' | 'descending' | null;
  currentSortBy?: string;
  currentSortDirection?: 'asc' | 'desc';
}

const SortableHeaderCell: React.FC<ISortableHeaderCellProps> = ({
  columnKey,
  children,
  onSort,
  getSortDirection,
  currentSortBy,
  currentSortDirection,
}) => (
  <Table.HeaderCell 
    style={{ cursor: 'pointer' }}
    onClick={() => onSort(columnKey)}
    sorted={getSortDirection(columnKey)}
  >
    {children}
    {currentSortBy === columnKey && (
      <Icon 
        name={currentSortDirection === 'asc' ? 'caret up' : 'caret down'} 
        style={{ marginLeft: '5px' }}
      />
    )}
  </Table.HeaderCell>
);


const ListTrackPayrolls: React.FC<IProps> = ({ match }) => {
  const rootStore = useContext(RootStoreContext);
  const {
    changePage,
    changePageSize,
    page,
    pageSize,
    payrolls,
    getTrackPayrolls,
    loadData,
    setSorting,
    filter,
  } = rootStore.trackPayrollStore;

  useEffect(() => {
    getTrackPayrolls();
  }, [getTrackPayrolls]);


  const handleSort = (columnName: string) => {
    setSorting(columnName);
  };

  const getSortDirection = (columnName: string): 'ascending' | 'descending' | null => {
    if (filter.sortBy === columnName) {
      return filter.sortDirection === 'asc' ? 'ascending' : 'descending';
    }
    return null;
  };

  if (!loadData) return <LoadingComponent content="Cargando nóminas..." />;

  return (
    <div>
      {payrolls.payrollList !== null && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div>
              <strong>Total de registros: {payrolls.length}</strong>
            </div>
          </div>

          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Acciones</Table.HeaderCell>
                <SortableHeaderCell
                  columnKey="PayrollType"
                  onSort={handleSort}
                  getSortDirection={getSortDirection}
                  currentSortBy={filter.sortBy}
                  currentSortDirection={filter.sortDirection}
                >
                  Tipo Nómina
                </SortableHeaderCell>
                <SortableHeaderCell
                  columnKey="DocumentID"
                  onSort={handleSort}
                  getSortDirection={getSortDirection}
                  currentSortBy={filter.sortBy}
                  currentSortDirection={filter.sortDirection}
                >
                  No. Documento
                </SortableHeaderCell>
                <SortableHeaderCell
                  columnKey="EmployeeIdentification"
                  onSort={handleSort}
                  getSortDirection={getSortDirection}
                  currentSortBy={filter.sortBy}
                  currentSortDirection={filter.sortDirection}
                >
                  Identificación Empleado
                </SortableHeaderCell>
                <SortableHeaderCell
                  columnKey="EmployeeName"
                  onSort={handleSort}
                  getSortDirection={getSortDirection}
                  currentSortBy={filter.sortBy}
                  currentSortDirection={filter.sortDirection}
                >
                  Nombre Empleado
                </SortableHeaderCell>
                <SortableHeaderCell
                  columnKey="DocumentValue"
                  onSort={handleSort}
                  getSortDirection={getSortDirection}
                  currentSortBy={filter.sortBy}
                  currentSortDirection={filter.sortDirection}
                >
                  Valor
                </SortableHeaderCell>
                <SortableHeaderCell
                  columnKey="IssueDate"
                  onSort={handleSort}
                  getSortDirection={getSortDirection}
                  currentSortBy={filter.sortBy}
                  currentSortDirection={filter.sortDirection}
                >
                  Fecha Emisión
                </SortableHeaderCell>
                <SortableHeaderCell
                  columnKey="ResultCode"
                  onSort={handleSort}
                  getSortDirection={getSortDirection}
                  currentSortBy={filter.sortBy}
                  currentSortDirection={filter.sortDirection}
                >
                  Estado
                </SortableHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {payrolls.payrollList.map((payroll) => (
                <Table.Row key={payroll.id}>
                  <Table.Cell textAlign="left">
                    <Link to={`${match}/${payroll.id}`}>
                      <Popup
                        basic
                        content="Detalle de nómina"
                        trigger={<Icon color="black" name="search plus" />}
                      />
                    </Link>
                    <Link to={`${match}/${payroll.id}/1`}>
                      <Popup
                        basic
                        content="Ver PDF"
                        trigger={<Icon color="black" name="file pdf" />}
                      />
                    </Link>
                  </Table.Cell>
                  <Table.Cell>{payroll.payrollType}</Table.Cell>
                  <Table.Cell>{payroll.documentID}</Table.Cell>
                  <Table.Cell>{payroll.employeeIdentification}</Table.Cell>
                  <Table.Cell>{payroll.employeeName}</Table.Cell>
                  <Table.Cell style={{ textAlign: "right" }}>
                    ${(payroll.documentValue || 0).toLocaleString("es-CO")}
                  </Table.Cell>
                  <Table.Cell>{payroll.issueDate}</Table.Cell>
                  <Table.Cell>
                    {payroll.resultCode === ProcessResultCode.PendingDianVerification ? (
                      <Icon name="clock" color="yellow" />
                    ) : (
                      <Icon
                        name={payroll.isValid ? "check circle" : "times circle"}
                        color={payroll.isValid ? "green" : "red"}
                      />
                    )}
                    {" "}
                    <Popup
                      content={payroll.resultCodeMessage}
                      trigger={<span>{getProcessResultCode(payroll.resultCode)}</span>}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              <label>Registros por página: </label>
              <Dropdown
                inline
                options={pageSizeOptions}
                value={pageSize}
                onChange={(e, data) => changePageSize(Number(data.value))}
              />
            </div>
            <Pagination
              activePage={page}
              onPageChange={(e, { activePage }) => changePage(Number(activePage))}
              totalPages={Math.ceil(payrolls.length / pageSize)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default observer(ListTrackPayrolls);
